import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AtSign, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CompanyLogo } from "@/components/common";
import { PersonChip } from "@/components/team/TeamBits";
import { OPEN_STATUSES, caseSum, daysUntil, nextAction, phaseLabel, longDate } from "@/lib/sk";

/** The one next step, as a real link into the right tab – quiet text that strengthens on hover, never a permanent button. */
export function ActionPill({ p, className }) {
  const a = nextAction(p);
  if (!a) return null;
  const to = a.key === "confirm" || a.key === "restore" ? `/p/${p.id}` : `/p/${p.id}?tab=${a.tab}`;
  return (
    <Link to={to} className={cn("relative z-20 inline-flex h-8 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[14px] font-semibold text-[hsl(var(--brand-strong))] transition-colors hover:bg-[hsl(var(--brand-soft))] focus-visible:ring-2 focus-visible:ring-ring/40", className)} data-testid={`work-action-${p.number}`} data-action={a.key}>
      {a.key === "reading" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
      <span className="truncate">{a.label}</span>
      <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  );
}

/** The story of the case in one line: where it is, how far, and anything that is actually pressing. */
function story(p) {
  const c = p.calc || {};
  const bits = [];
  if (p.status === "utkast") bits.push(p.docs_count ? `${p.docs_count} dokumenter · ${c.customer_items || 0} poster lest inn` : "ingen dokumenter ennå");
  else if (p.status === "forkastet") bits.push("forkastet før opprettelse");
  else if (c.customer_items && c.sum_kind === "preliminary") bits.push(`${c.coverage?.priced || 0} av ${c.coverage?.total || c.customer_items} poster priset`);
  else if (c.customer_items && c.sum_kind === "none") bits.push(`${c.customer_items} poster · ingen priset`);
  else if (p.quote) bits.push(`rev. ${p.quote.revision}${p.quote.status === "kontrollert" ? " · kontrollert" : ""}`);
  if (p.open_clarifications) bits.push(`${p.open_clarifications} ${p.open_clarifications === 1 ? "åpen avklaring" : "åpne avklaringer"}`);
  const d = daysUntil(p.tender_deadline);
  const open = OPEN_STATUSES.includes(p.status) || p.status === "utkast";
  let deadline = null;
  if (d !== null && open) deadline = d < 0 ? { text: "fristen er passert", tone: "bad" } : d === 0 ? { text: "frist i dag", tone: "warn" } : d <= 7 ? { text: `frist om ${d} ${d === 1 ? "dag" : "dager"}`, tone: "warn" } : { text: `frist ${longDate(p.tender_deadline)}`, tone: null };
  return { bits, deadline };
}

function TeamMarks({ p }) {
  const a = p.assignments || {};
  if (!p.mentions_for_me && !a.open) return null;
  const who = a.waiting_on || [];
  return (
    <span className="flex shrink-0 items-center gap-1.5" data-testid={`work-team-${p.number}`}>
      {p.mentions_for_me ? <span className="inline-flex h-5 items-center gap-0.5 rounded-full bg-[hsl(var(--brand))] px-1.5 font-mono text-[10.5px] font-semibold text-white" title={`${p.mentions_for_me} ${p.mentions_for_me === 1 ? "nytt notat nevner" : "nye notater nevner"} deg`} data-testid={`work-mentions-${p.number}`}><AtSign className="h-3 w-3" strokeWidth={2.4} />{p.mentions_for_me}</span> : null}
      {a.open ? <span className={cn("inline-flex items-center gap-1 text-[12.5px]", a.overdue ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground")} title={`Venter på ${who.map((w) => w.name).filter(Boolean).join(", ") || "tildelt person"}`} data-testid={`work-assignments-${p.number}`}><span className="flex items-center -space-x-1">{who.slice(0, 3).map((w) => <PersonChip key={w.id} person={w} size="xs" hideName className="rounded-full ring-1 ring-white" />)}</span></span> : null}
    </span>
  );
}

/**
 * One case as a short story, not a table row: title · customer · vessel, phase, how far it has come and what presses –
 * the sum on the right, the next step revealed on hover. The whole row opens the canvas.
 */
export function CaseRow({ p }) {
  const title = p.name || p.vessel?.name || `Sak ${p.number}`;
  const sub = [p.customer?.name || "Ukjent kunde", p.vessel?.name && p.vessel.name !== p.name ? p.vessel.name : null, `#${p.number}`].filter(Boolean).join(" · ");
  const { bits, deadline } = story(p);
  const phase = phaseLabel(p, { totals: p.calc, quote: p.quote });
  const sum = caseSum(p);
  return (
    <li className="group relative -mx-3 rounded-[14px] transition-colors hover:bg-black/[0.025] focus-within:bg-black/[0.03]" data-testid={`work-row-${p.number}`} data-status={p.status}>
      <Link to={`/p/${p.id}`} className="absolute inset-0 z-10 rounded-[14px] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40" aria-label={`Åpne ${title}`} data-testid={`work-row-link-${p.number}`} />
      <div className="flex items-start gap-4 px-3 py-4">
        <CompanyLogo name={p.customer?.name} domain={p.customer?.website} size="sm" className="mt-0.5 h-9 w-9 shrink-0 rounded-[10px] p-1" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[16px] font-semibold tracking-[-0.01em] text-foreground">{title}</div>
          <div className="mt-0.5 flex min-w-0 items-center gap-2 truncate text-[14px] text-muted-foreground"><span className="truncate">{sub}</span><TeamMarks p={p} /></div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-2 text-[14.5px] leading-[1.5]" data-testid={`work-status-${p.number}`} data-status={p.status}>
            <span className="text-foreground/85">{phase}</span>
            {bits.length ? <span className="text-muted-foreground">· {bits.join(" · ")}</span> : null}
            {deadline ? <span className={cn(deadline.tone === "bad" ? "font-medium text-[hsl(var(--v-blocked))]" : deadline.tone === "warn" ? "font-medium text-[hsl(var(--v-assumed))]" : "text-muted-foreground")} data-testid={`work-deadline-${p.number}`}>· {deadline.text}</span> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
          <div className={cn("font-mono text-[14.5px] tabular-nums", sum.kind === "none" ? "font-sans text-[13.5px] text-muted-foreground" : "text-foreground/90")} data-testid={`work-sum-${p.number}`} data-kind={sum.kind}>{sum.kind === "none" ? "" : `${sum.text} kr${sum.kind === "preliminary" ? " foreløpig" : ""}`}</div>
          <ActionPill p={p} className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 max-md:opacity-100" />
        </div>
      </div>
    </li>
  );
}
